import React, { useCallback, useEffect, useState } from 'react'

import { Button, useModalContext } from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import { LiveStreamKeysModal } from '../../components/LiveStreamKeysModal/LiveStreamKeysModal'
import { MuxPlayer } from '../../components/MuxPlayer'
import type { FormValues } from '../../types'
import * as S from './styled'

type Props = {
  startDate: string
  playbackId?: string
  playbackToken?: string
  rtmpsUrl?: string
  srtUrl?: string
  streamKey?: string
  loading: boolean
  onStartClick?: () => void
  onCountdownEnd: () => void
}

export const LiveStreamCountdown = ({
  rtmpsUrl,
  srtUrl,
  streamKey,
  startDate,
  loading,
  playbackId,
  playbackToken,
  onStartClick,
  onCountdownEnd,
}: Props) => {
  const [days, setDays] = useState(0)
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)

  const { values } = useFormikContext<FormValues>()

  const { open } = useModalContext()

  const setCountdown = useCallback(() => {
    var countDownDate = new Date(startDate).getTime()

    const now = new Date().getTime()

    const distance = countDownDate - now

    const days = Math.floor(distance / (1000 * 60 * 60 * 24))
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((distance % (1000 * 60)) / 1000)

    if (days > 0) {
      setDays(days)
    }
    if (hours > 0) {
      setHours(hours)
    }
    if (minutes > 0) {
      setMinutes(minutes)
    }
    if (seconds > 0) {
      setSeconds(seconds)
    }

    if (distance < 0) {
      onCountdownEnd()
    }
  }, [startDate, onCountdownEnd])

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown()
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [startDate, onCountdownEnd, setCountdown])

  useEffect(() => {
    setCountdown()
  })

  const poster =
    typeof values.videoThumbnail === 'object'
      ? URL.createObjectURL(values.videoThumbnail as File)
      : values.videoThumbnail

  if (playbackId && playbackToken) {
    return (
      <MuxPlayer
        playbackId={playbackId}
        tokens={{ playback: playbackToken }}
        streamType="live"
        poster={poster}
        autoPlay
        volume={0}
      >
        <S.Countdown>
          <S.CountdownText>
            <TimezonedDateString
              date={startDate}
              format="[Stream starts] MMM DD, YYYY, HH:mm A"
              withTimezoneAbbreviation={false}
            />
          </S.CountdownText>
          <S.CountdownBoxes>
            <S.CountdownBox>
              <S.CountdownBoxNumber>{days}</S.CountdownBoxNumber>
              <S.CountdownBoxLabel>days</S.CountdownBoxLabel>
            </S.CountdownBox>
            <S.CountdownBox>
              <S.CountdownBoxNumber>{hours}</S.CountdownBoxNumber>
              <S.CountdownBoxLabel>hours</S.CountdownBoxLabel>
            </S.CountdownBox>
            <S.CountdownBox>
              <S.CountdownBoxNumber>{minutes}</S.CountdownBoxNumber>
              <S.CountdownBoxLabel>mins</S.CountdownBoxLabel>
            </S.CountdownBox>
            <S.CountdownBox>
              <S.CountdownBoxNumber>{seconds}</S.CountdownBoxNumber>
              <S.CountdownBoxLabel>secs</S.CountdownBoxLabel>
            </S.CountdownBox>
          </S.CountdownBoxes>
          <Button
            appearance="primary"
            fullWidth
            label="Start now"
            loading={loading}
            onClick={onStartClick}
          />
        </S.Countdown>
      </MuxPlayer>
    )
  }

  return (
    <S.CountdownContainer>
      {poster && <S.CountdownPoster src={poster} />}
      <S.Countdown>
        <S.CountdownText>
          <TimezonedDateString
            date={startDate}
            format="[Stream starts] MMM DD, YYYY, HH:mm A"
            withTimezoneAbbreviation={false}
          />
        </S.CountdownText>
        <S.CountdownBoxes>
          <S.CountdownBox>
            <S.CountdownBoxNumber>{days}</S.CountdownBoxNumber>
            <S.CountdownBoxLabel>days</S.CountdownBoxLabel>
          </S.CountdownBox>
          <S.CountdownBox>
            <S.CountdownBoxNumber>{hours}</S.CountdownBoxNumber>
            <S.CountdownBoxLabel>hours</S.CountdownBoxLabel>
          </S.CountdownBox>
          <S.CountdownBox>
            <S.CountdownBoxNumber>{minutes}</S.CountdownBoxNumber>
            <S.CountdownBoxLabel>mins</S.CountdownBoxLabel>
          </S.CountdownBox>
          <S.CountdownBox>
            <S.CountdownBoxNumber>{seconds}</S.CountdownBoxNumber>
            <S.CountdownBoxLabel>secs</S.CountdownBoxLabel>
          </S.CountdownBox>
        </S.CountdownBoxes>
        <S.CountdownInfo>Waiting for connection...</S.CountdownInfo>
        <Button
          appearance="primary"
          fullWidth
          label="Show stream URL and key"
          loading={loading}
          onClick={() => {
            open(LiveStreamKeysModal, { rtmpsUrl, srtUrl, streamKey })
          }}
        />
      </S.Countdown>
    </S.CountdownContainer>
  )
}
